import { type ReactNode, useState } from "react";
import {
  Button,
  Heading,
  Icon,
  Modal,
  SimpleDialog,
  Text,
} from "@ttc3k/trekker";
import { Box, Center, Flex, Grid } from "styled-system/jsx";
import { NavArrowLeft, NavArrowRight, Tree, Xmark } from "iconoir-react";
import { SimpleLoader } from "shared/components/atoms";
import { css } from "styled-system/css";
import { useTranslation } from "react-i18next";
import type { ApolloError } from "@apollo/client";

export type SurveyResponseModalAccent = {
  banner: string;
  closeBg: string;
  closeBgHover: string;
};

type SurveyResponseModalProps = {
  isOpen: boolean;
  onClose: () => void;
  portal?: boolean;
  loading: boolean;
  error?: ApolloError;
  accent: SurveyResponseModalAccent;
  isNeutral: boolean;
  headerCard: ReactNode;
  children: ReactNode;
  onDelete?: () => void;
  deleteLoading?: boolean;
  deleteLabel: string;
  deleteDialogTitle: string;
  deleteDialogMessage: string;
  deleteDialogCancelLabel: string;
  primaryAction: {
    label: string;
    onClick: () => void;
    loading?: boolean;
    disabled?: boolean;
  };
  closeDisabled?: boolean;
  navigation?: {
    onPrev: (() => void) | null;
    onNext: (() => void) | null;
  };
  footerExtra?: ReactNode;
};

export const SurveyResponseModal = ({
  isOpen,
  onClose,
  portal,
  loading,
  error,
  accent,
  isNeutral,
  headerCard,
  children,
  onDelete,
  deleteLoading,
  deleteLabel,
  deleteDialogTitle,
  deleteDialogMessage,
  deleteDialogCancelLabel,
  primaryAction,
  closeDisabled,
  navigation,
  footerExtra,
}: SurveyResponseModalProps) => {
  const { t: tCore } = useTranslation("core", {
    keyPrefix: "MODAL_BUTTON_COPY",
  });

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const bannerColor = isNeutral ? "bg.lighter" : accent.banner;
  const closeBg = isNeutral ? "bg.lighter" : accent.closeBg;
  const closeBgHover = isNeutral ? "bg.light" : accent.closeBgHover;
  const closeIconColor = isNeutral ? "icon.dark" : "icon.inverse";

  return (
    <>
      <Modal
        open={isOpen}
        onOpenChange={onClose}
        placement="rightLargePane"
        portal={portal}
        closeTriggerProps={{
          className: css({
            h: "24px",
            w: "24px",
            backdropFilter: "blur(4px)",
            bgColor: closeBg,
            borderRadius: "full",
            p: "4px",
            color: "white",
            _hover: { bgColor: closeBgHover },
            _focusVisible: { bgColor: closeBgHover },
          }),
          style: { transition: "background-color 0.2s ease-in-out" },
          children: (
            <Icon
              Element={Xmark}
              w="16px"
              h="16px"
              color={closeIconColor}
            />
          ),
          disabled: closeDisabled,
        }}
      >
        <Grid
          gridTemplateRows="1fr auto"
          h="full"
          gap="0px"
          gridGap="0px"
          gridRowGap="0px"
          overflow="hidden"
        >
          <Box position="relative" overflowY="auto">
            <Box
              position="absolute"
              top="0"
              left="0"
              right="0"
              w="full"
              h="200px"
              bgColor={bannerColor}
              style={{ transition: "background-color 0.2s ease-in-out" }}
            />
            <Flex
              flexDirection="column"
              gap="500"
              p="500"
              pt="96px"
              position="relative"
              zIndex={1}
            >
              {loading ? (
                <SimpleLoader />
              ) : error ? (
                <Center
                  maxW="600px"
                  gap="200"
                  flexDir="column"
                  h="full"
                  px="500"
                >
                  <Icon
                    Element={Tree}
                    w="40px"
                    h="40px"
                    color="icon.success.mid"
                  />
                  <Heading size="h5">{tCore("ERROR.TITLE")}</Heading>
                  <Text textAlign="center" color="text.mid">
                    {error.message}
                  </Text>
                </Center>
              ) : (
                <>
                  {headerCard}
                  {children}
                </>
              )}
            </Flex>
          </Box>

          <Flex
            flexDir="column"
            borderTopWidth="1px"
            borderColor="border.light"
            borderStyle="solid"
            boxShadow="0px 0px 8px 0px rgba(0, 0, 0, 0.10)"
          >
            {footerExtra && (
              <Box px="500" pt="300" pb="150">
                {footerExtra}
              </Box>
            )}
          <Flex
            px="500"
            py="300"
            justifyContent="space-between"
            alignItems="center"
            gap="300"
          >
            {onDelete ? (
              <Button
                visual="destructive"
                mode="light"
                type="button"
                disabled={primaryAction.loading}
                isLoading={deleteLoading}
                onClick={() => setIsDeleteConfirmOpen(true)}
              >
                {deleteLabel}...
              </Button>
            ) : (
              <div />
            )}
            {navigation ? (
              <Flex gap="100">
                <Button
                  mode="light"
                  type="button"
                  size="sm"
                  disabled={!navigation.onPrev}
                  onClick={navigation.onPrev ?? undefined}
                  aria-label="Previous"
                >
                  <Icon Element={NavArrowLeft} />
                </Button>
                <Button
                  mode="light"
                  type="button"
                  size="sm"
                  disabled={!navigation.onNext}
                  onClick={navigation.onNext ?? undefined}
                  aria-label="Next"
                >
                  <Icon Element={NavArrowRight} />
                </Button>
              </Flex>
            ) : (
              <div />
            )}
            <Button
              visual="accent.blue"
              type="button"
              disabled={primaryAction.disabled || deleteLoading}
              isLoading={primaryAction.loading}
              onClick={primaryAction.onClick}
            >
              {primaryAction.label}
            </Button>
          </Flex>
          </Flex>
        </Grid>
      </Modal>

      {onDelete && (
        <SimpleDialog
          open={isDeleteConfirmOpen}
          title={deleteDialogTitle}
          message={deleteDialogMessage}
          cancelButtonCopy={deleteDialogCancelLabel}
          onCancelClick={() => setIsDeleteConfirmOpen(false)}
          confirmButtonCopy={deleteLabel}
          confirmButtonVisual="destructive"
          onConfirmClick={() => {
            setIsDeleteConfirmOpen(false);
            onDelete();
          }}
          portal
        />
      )}
    </>
  );
};
